// Огледало на бекенд валидациите. Целта — да хващаме грешки преди заявка,
// но true source of truth остава бекендът (back-end-ът никога не вярва на клиента).

const EIK_W_9_A = [1, 2, 3, 4, 5, 6, 7, 8];
const EIK_W_9_B = [3, 4, 5, 6, 7, 8, 9, 10];
const EIK_W_13_A = [2, 7, 3, 5];
const EIK_W_13_B = [4, 9, 5, 7];

/**
 * Суровият остатък по модул 11 — 10 се ВРЪЩА, не се свива до 0.
 *
 * Свиването живее в `controlDigit`, защото стойността 10 е точно сигналът за
 * втора обиколка с другите тегла. Свито тук, то правеше втората обиколка
 * недостижима и всеки единадесети ЕИК излизаше невалиден.
 */
const remainderMod11 = (digits: number[], weights: number[]): number =>
  digits.reduce((sum, digit, index) => sum + digit * weights[index], 0) % 11;

/**
 * Контролната цифра по алгоритъма на bulstat.bg: първо с теглата А; само ако
 * остатъкът е 10, се смята пак с теглата Б; ако и там излезе 10 — цифрата е 0.
 */
const controlDigit = (
  digits: number[],
  weightsA: number[],
  weightsB: number[],
): number => {
  const first = remainderMod11(digits, weightsA);
  const remainder = first === 10 ? remainderMod11(digits, weightsB) : first;
  return remainder === 10 ? 0 : remainder;
};

export const isValidEIK = (raw: string): boolean => {
  const value = raw.trim();
  if (!/^\d+$/.test(value)) return false;
  const digits = value.split("").map(Number);
  if (digits.length === 9) {
    return controlDigit(digits.slice(0, 8), EIK_W_9_A, EIK_W_9_B) === digits[8];
  }
  if (digits.length === 13) {
    if (!isValidEIK(value.slice(0, 9))) return false;
    return (
      controlDigit(digits.slice(8, 12), EIK_W_13_A, EIK_W_13_B) === digits[12]
    );
  }
  return false;
};

export const isValidBgVat = (raw: string): boolean =>
  /^BG\d{9,10}$/.test(raw.replace(/\s+/g, "").toUpperCase());

export const isValidBIC = (raw: string): boolean =>
  /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(
    raw.replace(/\s+/g, "").toUpperCase(),
  );

const ibanMod97 = (iban: string): number => {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let numeric = "";
  for (const c of rearranged) {
    numeric += /[A-Z]/.test(c) ? (c.charCodeAt(0) - 55).toString() : c;
  }
  // BigInt mod — IBAN-ите са по-дълги от 32-битов int.
  let rem = 0n;
  for (const ch of numeric) rem = (rem * 10n + BigInt(ch)) % 97n;
  return Number(rem);
};

export const isValidIBAN = (raw: string): boolean => {
  const v = raw.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(v)) return false;
  return ibanMod97(v) === 1;
};

// Често срещано: оператор пише ДДС № с кирилица „БГ" вместо латински „BG".
// Normalize-ваме типичните визуално-сходни букви, така че валидацията да
// минава без да е нужно ръчно превключване на клавиатура.
export const normalizeVatInput = (raw: string): string =>
  raw
    .replace(/[Бб]/g, "B")
    .replace(/[Гг]/g, "G")
    .replace(/\s+/g, "")
    .toUpperCase();

export const isValidEmail = (raw: string): boolean =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(raw.trim());

export const isValidProforma = (raw: string): boolean =>
  /^[A-ZА-Я]-\d{4}-\d{4,}$/.test(raw.trim().toUpperCase());
